import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { PollIcon } from '@/components/icons';
import type { PollSummary } from '@/types/api';

/**
 * In-bubble render of a poll. Lives inside the message body — replaces
 * the plain markdown for poll messages (the parent message's content is
 * just the question, repeated here as the header). Three states:
 *
 *   open       — vote buttons live, click toggles your vote
 *   closed     — bars + counts only, no buttons; "Закрыто" badge
 *   mine+open  — extra "Закрыть" action visible for the author
 *
 * Multi-choice polls let the user toggle any subset; the server enforces
 * a single replace-set per request, so we re-PUT the full optionIds set
 * every click. Single-choice picks just send `[optionId]`.
 */
export function PollBubble(props: {
  poll: PollSummary;
  messageId: string;
  channelId: string;
  messageAuthorId: string;
}) {
  const { t } = useTranslation();
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const upsertMessage = useDataStore((s) => s.upsertMessage);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // The poll is read straight from props (= message store). When we
  // mutate it (vote / close), we splice the API response back into the
  // store via upsertMessage; that fires the subscription and triggers
  // a re-render with the new props.poll. No local `view` state — keeps
  // us from fighting an HMR-induced sync-from-props race that caused
  // votes to visually no-op until reload.
  const view = props.poll;

  const isMine = meId === props.messageAuthorId;
  const isClosed = !!view.closedAt;
  const totalVotes = view.options.reduce((sum, o) => sum + o.voteCount, 0);

  /** Splice a freshly-returned poll back into the parent message in
   *  the store. upsertMessage REPLACES the message by id (not merges),
   *  so we read the existing row first and only override `poll`.
   *  That keeps the author / reactions / replyTo / etc intact. */
  function applyPoll(next: PollSummary) {
    const existing = useDataStore
      .getState()
      .messagesByChannel[props.channelId]?.find((m) => m.id === props.messageId);
    if (!existing) return;
    upsertMessage({ ...existing, poll: next });
  }

  async function toggleOption(optionId: string) {
    if (isClosed || busy) return;
    const myCurrent = new Set(view.myOptionIds);
    if (view.multiChoice) {
      if (myCurrent.has(optionId)) myCurrent.delete(optionId);
      else myCurrent.add(optionId);
    } else {
      if (myCurrent.has(optionId) && myCurrent.size === 1) {
        myCurrent.clear();
      } else {
        myCurrent.clear();
        myCurrent.add(optionId);
      }
    }
    const optionIds = [...myCurrent];
    setBusy(true);
    try {
      const updated = await api.votePoll(view.id, optionIds);
      applyPoll(updated);
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.poll.voteError')));
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    if (isClosed || busy) return;
    setConfirmClose(false);
    setBusy(true);
    try {
      const updated = await api.closePoll(view.id);
      applyPoll(updated);
      toast.success(t('cmod.poll.closed'));
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.poll.closeError')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="studio-poll mt-1.5 flex min-w-0 flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <PollIcon className="h-3.5 w-3.5" />
        <span>
          {view.anonymous ? t('cmod.poll.badgeAnon') : t('cmod.poll.badge')}
          {view.multiChoice && ` · ${t('cmod.poll.badgeMulti')}`}
          {isClosed && ` · ${t('cmod.poll.badgeClosed')}`}
        </span>
      </div>
      <div className="text-sm font-medium tracking-[-0.01em] text-foreground">
        {view.question}
      </div>
      <div className="flex flex-col gap-1.5">
        {view.options.map((o) => {
          const picked = view.myOptionIds.includes(o.id);
          const pct = totalVotes > 0 ? Math.round((o.voteCount / totalVotes) * 100) : 0;
          return (
            <button
              key={o.id}
              type="button"
              disabled={isClosed || busy}
              aria-pressed={picked}
              onClick={() => void toggleOption(o.id)}
              className={
                'group relative overflow-hidden rounded-lg px-3 py-2 text-left text-xs transition-colors disabled:cursor-default ' +
                (picked
                  ? 'bg-accent/15'
                  : isClosed
                    ? 'bg-default'
                    : 'bg-default hover:bg-default/70')
              }
            >
              {/* Background-fill bar showing the option's share. We render
                  it behind the text so reads as a horizontal progress
                  meter without being a separate row. */}
              <span
                aria-hidden
                className={
                  'pointer-events-none absolute inset-y-0 left-0 transition-[background-color,border-color,color,box-shadow,opacity] ' +
                  (picked ? 'bg-accent/25' : 'bg-foreground/10')
                }
                style={{ width: `${pct}%` }}
              />
              <span className="relative flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  {/* Single-choice radio / multi-choice check indicator —
                      same square so it doesn't change shape on toggle. */}
                  <span
                    className={
                      'grid h-4 w-4 shrink-0 place-items-center rounded-full text-[10px] ' +
                      (picked
                        ? 'bg-accent text-bg'
                        : 'ring-1 ring-ink-muted/40 text-transparent')
                    }
                  >
                    ✓
                  </span>
                  <span className="font-light text-ink">{o.label}</span>
                </span>
                <span className="flex items-center gap-2 text-xs text-ink-muted tabular-nums">
                  <span className="tabular-nums">{pct}%</span>
                  <span className="text-ink-dim">·</span>
                  <span>{o.voteCount}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2 pt-0.5 text-xs text-ink-muted">
        <span>
          {t('cmod.poll.voted', { count: view.totalVoters })}
          {view.totalVoters > 0 ? '' : ` ${t('cmod.poll.votedNone')}`}
        </span>
        {isMine && !isClosed && (
          confirmClose ? (
            <span className="flex items-center gap-1.5">
              <span className="text-ink-muted">{t('cmod.poll.closeConfirm')}</span>
              <button
                type="button"
                onClick={() => void close()}
                disabled={busy}
                className="font-medium rounded px-1.5 py-0.5 text-danger transition-colors hover:bg-danger/15 disabled:opacity-40"
              >
                {t('cmod.poll.yes')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmClose(false)}
                className="rounded px-1.5 py-0.5 text-ink-muted transition-colors hover:text-ink"
              >
                {t('cmod.poll.no')}
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmClose(true)}
              disabled={busy}
              className="rounded px-2 py-0.5 text-danger transition-colors hover:bg-danger/15 disabled:opacity-40"
            >
              {t('cmod.poll.close')}
            </button>
          )
        )}
      </div>
    </div>
  );
}
